// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import { doSignOut, extractErrorMessage, type User, useEvolvePost } from '@evolvemep/api-ts';
import { Title } from '@mantine/core';
// oxlint-disable-next-line no-restricted-imports
import { Navigate, useLocation, useNavigate } from 'react-router';

import { EULAModal } from 'app/EULAModal';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { setLocalStorageWrapped } from 'hooks/useLocalStorage';
import { useMaybeUser } from 'hooks/user/useMaybeUser';

import { useConfirmUserSso, useGetTokenSso } from '../auth/restCalls';
import { buildDesktopAuthUrl, buildLoopbackCallbackUrl, parseDesktopAuthParams } from '../DesktopAuth/common';
import { readSsoCallbackParams } from './helper';

type SsoCallbackParams = ReturnType<typeof readSsoCallbackParams>;

type DesktopSsoCallbackProps = Pick<SsoCallbackParams, 'ssoCode'> & {
  desktopRequest: string;
};

/** Finishes an SSO sign-in started on `/desktop-auth`, without touching the website's own session. */
const DesktopSsoCallback = ({ ssoCode, desktopRequest }: DesktopSsoCallbackProps) => {
  // useEvolveNavigate only works inside Shop routes
  const navigate = useNavigate();
  const { apiCall: createDesktopCodeFromSsoApiCall } = useEvolvePost('admin/authentication/desktop/code/sso', {
    alertOnError: false,
  });

  useEffect(() => {
    const request = parseDesktopAuthParams(new URLSearchParams(desktopRequest));
    if (isNil(ssoCode) || !request.valid) {
      void navigate(buildDesktopAuthUrl(desktopRequest, 'AuthorizationCodeInvalidOrExpired'), { replace: true });
      return;
    }

    const { clientId, redirectUri, state, codeChallenge } = request;
    createDesktopCodeFromSsoApiCall({
      authorizationCode: ssoCode,
      clientId,
      codeChallenge,
      codeChallengeMethod: 'S256',
      redirectUri,
    })
      .then(({ code }) => window.location.replace(buildLoopbackCallbackUrl(redirectUri, { code, state })))
      .catch((error) => {
        // oxlint-disable-next-line no-console
        console.error('Could not create a desktop authorization code from the sso authentication code.', error);
        // `invalid_grant` is the OAuth name for an SSO code the API could not exchange; anything else is a
        // validation message worth showing as-is (e.g. an unregistered client ID).
        const message = extractErrorMessage(error);
        const errorCode = message === 'invalid_grant' ? 'AuthorizationCodeInvalidOrExpired' : message;
        void navigate(buildDesktopAuthUrl(desktopRequest, errorCode), { replace: true });
      });
  }, [createDesktopCodeFromSsoApiCall, desktopRequest, navigate, ssoCode]);

  return <Title fz="h3">Authenticating, please wait!</Title>;
};

const WebSsoCallback = ({ ssoCode, state }: SsoCallbackParams) => {
  const { user: signedInUser, refreshUser } = useMaybeUser();
  // useEvolveNavigate only works inside Shop routes
  const navigate = useNavigate();
  const { confirmUserSso } = useConfirmUserSso();
  const { getTokenSso } = useGetTokenSso();
  const redirect = state.previousLoc ?? '/';

  useEffect(() => {
    const checkEulaAccepted = async (user: User) => {
      if (isNil(user.confirmedAt)) {
        doSignOut('/auth/login?errorCode=EmailNotVerified');
        return;
      }

      if (user.acceptedLatestEula) {
        void navigate(redirect);
      }
    };

    const removeCodeParamFromUrl = () => {
      const searchParams = new URLSearchParams(location.search);

      // Remove the specific parameter
      searchParams.delete('code');

      // Update the URL without the removed parameter
      void navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true });
    };

    if (isNotNil(ssoCode)) {
      removeCodeParamFromUrl();
      getTokenSso(ssoCode)
        .then(async (authResult) => {
          localStorage.setItem('STAY_SIGNED_IN', 'true');
          setLocalStorageWrapped('EVOLVE_ACCESS_TOKEN', authResult.accessToken);
          setLocalStorageWrapped('EVOLVE_REFRESH_TOKEN', authResult.refreshToken);
          setLocalStorageWrapped('EVOLVE_TOKEN_USERNAME', authResult.tokenUserName);

          const { userId, confirmationCode } = state;

          if (isNotNil(userId) && isNotNil(confirmationCode)) {
            await confirmUserSso({
              userName: authResult.tokenUserName,
              userId,
              confirmationCode,
            }).catch((e) => {
              // oxlint-disable-next-line no-console
              console.error('Could not accept invite directly, user is likely not confirmed.', e);
            });
          }
          const user = await refreshUser();

          if (isNil(user)) {
            doSignOut('/auth/login?errorCode=UserNotFoundException');
            return;
          }

          await checkEulaAccepted(user);
        })
        .catch((error) => {
          // oxlint-disable-next-line no-console
          console.error('Could not make the authentication by the sso authentication code.', error);
          doSignOut('/auth/login?errorCode=AuthorizationCodeInvalidOrExpired');
        });
    }
  }, [confirmUserSso, getTokenSso, navigate, redirect, refreshUser, ssoCode, state]);

  if (signedInUser?.acceptedLatestEula) {
    return <Navigate to={redirect} />;
  }

  return (
    <>
      <Title fz="h3">Authenticating, please wait!</Title>
      <EULAModal onEulaAccepted={() => navigate(redirect)} />
    </>
  );
};

export const LoginMicrosoftSSO = () => {
  const { search, hash } = useLocation();
  const { ssoCode, state } = useMemo(() => readSsoCallbackParams({ search, hash }), [search, hash]);
  return isNotNil(state.desktopRequest) ? (
    <DesktopSsoCallback ssoCode={ssoCode} desktopRequest={state.desktopRequest} />
  ) : (
    <WebSsoCallback ssoCode={ssoCode} state={state} />
  );
};
